import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Avatar, Divider } from "@mui/material";
import DateDisplay from "../components/common/DateDisplay";
import EmailLetter from "../components/email/EmailLetter";
import { useGetMemberEmail } from "services/email";
import { EmailMessage } from "protogen/conversation_pb";
import Loading from "../components/common/Loading";
import FlexPage from "components/layout/FlexPage";
import Breadcrumbs from "components/common/Breadcrumbs";
import useIsMobile from "components/hooks/useIsMobile";
import EmailAgentDisplay from "components/email/EmailAgentDisplay";

export default () => {
  const isMobile = useIsMobile();
  const { emailRef } = useParams<{ emailRef: string }>();
  const [email, setEmail] = useState<EmailMessage | null>(null);
  const { request, loading } = useGetMemberEmail();

  const loadEmail = async () => {
    if (!emailRef) return;
    const response = await request({ emailRef });
    if (response?.email) {
      setEmail(response.email);
    }
  };

  useEffect(() => {
    if (emailRef) {
      loadEmail();
    }
  }, [emailRef]);

  if (loading) {
    return <Loading />;
  }

  if (!email) {
    return null;
  }

  return (
    <FlexPage leftAligned sx={{ width: "100%", alignItems: "flex-start" }}>
      <Breadcrumbs
        breadcrumbs={[{ name: "Home", link: "/" }, { name: "Emails" }]}
      />
      <Box
        display="flex"
        flexDirection="column"
        gap="20px"
        sx={{ marginTop: isMobile ? "12px" : "32px", width: "100%" }}
      >
        {/* Email Subject */}
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="start"
          gap="15px"
        >
          <Typography
            variant="h3"
            sx={{
              color: "#101828",
              fontSize: "20px",
              fontWeight: 500,
              flex: 1,
            }}
          >
            {email.subject}
          </Typography>
        </Box>

        <Divider />

        {/* Email Header */}
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="start"
          gap="15px"
        >
          <Box
            display="flex"
            flexDirection="row"
            gap="12px"
            alignItems="start"
            flex={1}
          >
            <Avatar sx={{ height: 40, width: 40, fontSize: "16px" }}>
              {email.senderDisplayName[0]}
            </Avatar>
            <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
              <Typography variant="bodyHeavy" sx={{ opacity: ".8" }}>
                {email.senderDisplayName}
              </Typography>
              <Typography variant="bodySmall" sx={{ color: "#616161" }}>
                to {email.toAddresses.join(", ")}
                {email.ccAddresses.length > 0 && (
                  <>, cc {email.ccAddresses.join(", ")}</>
                )}
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="end"
            gap="6px"
          >
            <DateDisplay
              date={new Date(Number(email.timestampSec) * 1000)}
              color="#475467"
              sx={{
                fontSize: "13px",
                fontStyle: "italic",
              }}
            />
          </Box>
        </Box>
        {email.agentThreadRef && (
          <EmailAgentDisplay threadRef={email.agentThreadRef} />
        )}

        {/* Email Content */}
        <Box>
          <EmailLetter email={email} />
        </Box>
      </Box>
    </FlexPage>
  );
};
